import { useParams } from "react-router-dom";
import Table from "../../Components/Table";
import StatusHandler from "../../Components/Table/StatusHandler";
import { useQuery } from "react-query";
import { getUserDetails } from "../../Services/User";
import { getAllRides } from "../../Services/Ride";
import { useEffect, useState } from "react";
import useTableControls from "../../Hooks/useTableControls";
import useFetchData from "../../Hooks/useFetchData";
import { format_date, getDriverDisplayName, getSerialNumber, resolveImageUrl } from "../../Utils/helpers";
import BackButton from "../../Components/BackButton";
import OpenSupportChatButton from "../../Components/OpenSupportChatButton";
import ModalImageViewer from "../../Components/ModalImageViewer";
import "../Dashboard/Dashboard.css";
import { formatBookingCode } from "../../Utils/bookingCode";
import AccountDeletionBadge, { isAccountDeleted } from "../../Components/AccountDeletionBadge";
import AccountDeletionActions from "../../Components/AccountDeletionActions";
import LastLogin from "../../Components/LastLogin";

const AVATAR_FALLBACK = "images/avatar.png";

export default function UserDetails() {
  const { id } = useParams();
  const [user, setUser] = useState({});
  const [viewerShow, setViewerShow] = useState(false);
  const [profileImageSrc, setProfileImageSrc] = useState(AVATAR_FALLBACK);
  const {
    perPage,
    setPerPage,
    status,
    setStatus,
    search_string,
    setSearchString,
    from,
    setFrom,
    to,
    setTo,
  } = useTableControls();

  const { data, refetch: refetchUser } = useQuery(["user_details", id], () => getUserDetails(id));

  useEffect(() => {
    if (data?.data?.data?.docs?.length > 0)
      setUser(data?.data?.data?.docs?.[0]);
  }, [data?.data?.data?.docs]);

  useEffect(() => {
    setProfileImageSrc(
      resolveImageUrl(user?.profileImage, AVATAR_FALLBACK)
    );
  }, [user?.profileImage]);

  const {
    page,
    setPage,
    isFetching: rideFetching,
    isLoading: rideLoading,
    data: rideData,
  } = useFetchData("user_ride_logs", getAllRides, [
    perPage,
    search_string,
    status,
    from,
    to,
    id,
  ]);

  const fullNameStr = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const fullName = fullNameStr || <span className="text-muted" style={{ fontSize: '1.5rem', fontWeight: 500 }}>Profile Incomplete</span>;
  const totalBookings = rideData?.data?.data?.totalDocs || 0;
  const memberSince = user?.createdAt
    ? format_date(user.createdAt, "MMM YYYY")
    : null;

  const missingEmailText = isAccountDeleted(user) ? "Removed" : user?.socialProvider === "apple" ? "Not shared (Apple)" : "Profile Incomplete";
  const isIncomplete = !user?.firstName || (!user?.email && user?.socialProvider !== "apple");

  const statItems = [
    { label: "Phone Number", value: user?.phone || "N/A" },
    { label: "Email Address", value: user?.email || <span className="text-muted" style={{ fontSize: '0.85rem' }}>{missingEmailText}</span> },
    { label: "Total Bookings", value: totalBookings, highlight: true },
    {
      label: "Date Joined",
      value: user?.createdAt ? format_date(user.createdAt, "DD/MM/YYYY") : "N/A",
    },
    { label: "Last Login", value: <LastLogin user={user} /> },
  ];

  const handleProfileImageError = () => {
    setProfileImageSrc(AVATAR_FALLBACK);
  };

  const openProfileViewer = () => {
    if (profileImageSrc && profileImageSrc !== AVATAR_FALLBACK) {
      setViewerShow(true);
    }
  };

  return (
    <div className="app-content content dashboard">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="configuration">
            <div className="row">
              <div className="col-12">
                <div className="user-details-wrapper">
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="back-title d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center">
                          <BackButton />
                          <h1 className="pageTitle m-0 ms-3 fw-800 fs-2">User Details</h1>
                        </div>
                        <div className="back-title-actions">
                          <AccountDeletionActions user={user} label="user account" onChanged={() => refetchUser()} />
                          <OpenSupportChatButton
                            participantId={user?._id || id}
                            chatTab="users"
                            participantName={fullNameStr || "User"}
                            participantAvatar={profileImageSrc}
                            participantRole="User"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-4 mb-lg-5">
                    <div className="col-12">
                      <div className="user-profile-card details-card-premium">
                        <div className="user-profile-banner" />
                        <div className="user-profile-body">
                          <div className="user-profile-top d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 gap-md-4">
                            <button
                              type="button"
                              className="user-profile-avatar-btn"
                              onClick={openProfileViewer}
                              aria-label="View profile photo"
                            >
                              <div className="user-profile-avatar-wrap">
                                <img
                                  src={profileImageSrc}
                                  alt={fullNameStr || "User"}
                                  className="userImage"
                                  onError={handleProfileImageError}
                                />
                              </div>
                            </button>

                            <div className="user-profile-meta flex-grow-1 text-center text-md-start w-100">
                              <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-start justify-content-between gap-2 gap-sm-3 mb-2">
                                <div>
                                  <h2 className="user-profile-name text-capitalize mb-1">
                                    {fullName}
                                  </h2>
                                  <p className="user-profile-email mb-0">{user?.email || <span className="text-muted" style={{ fontSize: '0.85rem' }}>{missingEmailText}</span>}</p>
                                  {memberSince && (
                                    <p className="user-profile-member mb-0">
                                      Member since {memberSince}
                                    </p>
                                  )}
                                </div>
                                <div className="d-flex flex-wrap align-items-center gap-1 flex-shrink-0">
                                  {!isAccountDeleted(user) && (
                                    <span
                                      className={`status-tag flex-shrink-0 ${isIncomplete ? "orange-tag" : (user?.active ? "green-tag" : "red-tag")}`}
                                    >
                                      {isIncomplete ? "Incomplete" : (user?.active ? "Active" : "Inactive")}
                                    </span>
                                  )}
                                  <AccountDeletionBadge item={user} className="" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="user-profile-stats">
                            {statItems.map((item) => (
                              <div key={item.label} className="user-profile-stat">
                                <span className="user-profile-stat-label">{item.label}</span>
                                <span
                                  className={`user-profile-stat-value ${item.highlight ? "is-highlight" : ""}`}
                                >
                                  {item.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Table
                    key="user-details-rides-table"
                    tableTitles={[
                      "S.No.",
                      "Booking ID",
                      "Driver Name",
                      "Date",
                      "Pickup Location",
                      "Drop-Off Location",
                      "Status",
                      "Action",
                    ]}
                    dateFilter
                    showEntries
                    sortBy
                    isLoading={rideLoading}
                    isFetching={rideFetching}
                    data={rideData?.data?.data?.docs}
                    totalPages={Math.ceil(rideData?.data?.data?.totalPages || 0)}
                    totalDocs={rideData?.data?.data?.totalDocs}
                    page={page}

                    setPage={setPage}
                    setSearchString={setSearchString}
                    status={status}
                    setStatus={setStatus}
                    perPage={perPage}
                    setPerPage={setPerPage}
                    from={from}
                    setFrom={setFrom}
                    to={to}
                    setTo={setTo}
                    status_options={[
                      { label: "Pending", value: "pending" },
                      { label: "Accepted", value: "accepted" },
                      { label: "Rejected", value: "rejected" },
                      { label: "Started", value: "started" },
                      { label: "Completed", value: "completed" },
                      { label: "Cancelled", value: "cancelled" },
                    ]}
                  >
                    {!rideLoading &&
                      rideData?.data?.data?.docs?.map((item, index) => (
                        <tr key={item?._id || index}>
                          <td>{getSerialNumber(rideData?.data?.data, index)}</td>
                          <td>{formatBookingCode(item)}</td>
                          <td className="text-capitalize">{getDriverDisplayName(item?.driver)}</td>
                          <td>
                            {item?.createdAt &&
                              format_date(item?.createdAt, "MM/DD/YYYY")}
                          </td>
                          <td>{item?.bookingDetails?.pickupLocation?.name}</td>
                          <td>{item?.bookingDetails?.dropOffLocation?.name}</td>
                          <td>{item.status}</td>
                          <td>
                            <StatusHandler
                              key={`${item?._id}-status`}
                              view
                              item={item}
                              redirectUrl={`/ride-management/ride-details/${item?._id}`}
                            />
                          </td>
                        </tr>
                      ))}
                  </Table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <ModalImageViewer
        show={viewerShow}
        images={[profileImageSrc]}
        startIndex={0}
        onHide={() => setViewerShow(false)}
        title="Profile Photo"
      />
    </div>
  );
}
